import axios from "axios";
import Swal from "sweetalert2";

const BIOSENSE_API_URL = process.env.REACT_APP_BIOSENSE_API_URL;

const handleRequest = async (method, url, body = {}) => {
  try {
    // Combine the base URL and the relative URL
    const fullUrl = `${BIOSENSE_API_URL}${url}`;
    console.log(fullUrl);

    // Retrieve the token from localStorage
    const token = localStorage.getItem("token");

    let headers = {
      "Content-Type": "application/json",
    };

    // Add the Authorization header if the token exists
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    let config = {
      url: fullUrl,
      method: method.toLowerCase(),
      headers: headers,
    };

    // Include the body only for methods that support it
    if (["post", "put"].includes(method.toLowerCase())) {
      config.data = body;
    }

    // Make the request using Axios
    const response = await axios(config);

    // Return the response data
    return response.data;
  } catch (error) {
    const errorMsg =
      error.response?.data?.error || error.request?.statusText || error.message || "Network error";

    throw new Error(errorMsg);
  }
};

const biosenseAPI = {
  login: ({ username, password }) => {
    return handleRequest(`post`, `/auth/login`, { username, password });
  },
  getDeviceList: () => {
    return handleRequest(`get`, `/devices/getself`);
  },
  getDeviceListByProject: ({ project_id }) => {
    return handleRequest(`post`, `/devices/getbyprojectid`, { project_id });
  },
  getDeviceDetails: ({ sn }) => {
    return handleRequest(`post`, `/devices/getbysn`, { sn });
  },
  getProjectDetails: ({ id }) => {
    return handleRequest(`post`, `/projects/getbyid`, { id });
  },
  getUserProfile: () => {
    return handleRequest(`get`, `/users/getself`);
  },
  getProjectList: () => {
    return handleRequest(`get`, `/projects/getself`);
  },
  getNotifSettings: () => {
    return handleRequest(`get`, `/notif/getself`);
  },
  getActivityList: () => {
    return handleRequest(`get`, `/activities/getself`);
  },
  getDeviceValueListLatest: ({ sn }) => {
    return handleRequest(`post`, `/devicehistory/getDeviceValueListLatest`, { sn });
  },
  getDeviceDescription: ({ sn }) => {
    return handleRequest(`post`, `/devicehistory/getDeviceDescription`, { sn });
  },
  getDeviceMode: ({ sn }) => {
    return handleRequest(`post`, `/devicehistory/getDeviceMode`, { sn });
  },
  getAlertSettings: ({ sn }) => {
    return handleRequest(`post`, `/alertsetting/getbydevicesn`, { sn });
  },
  getHistoryValueByTimeRangeAndTypes: ({ sn, from, to, types }) => {
    return handleRequest(`post`, `/devicehistory/getHistoryValueByTimeRangeAndTypes`, {
      sn,
      from,
      to,
      types,
    });
  },
  getPendingAlertList: ({ includeIgnoredAlerts = true }) => {
    return handleRequest(`post`, `/alert/getselfpending`, { includeIgnoredAlerts });
  },
  resolveAlert: ({ alert_id }) => {
    return handleRequest(`post`, `/alert/resolve`, { alert_id });
  },
  ignoreAlert: ({ alert_id }) => {
    return handleRequest(`post`, `/useralertignore/addignore`, { alert_id })
  },
  removeIgnoreAlert: ({ alert_id }) => {
    return handleRequest(`post`, `/useralertignore/removeignore`, { alert_id })
  },
  updateUserProfile: ({ field, value }) => {
    return handleRequest(`post`, `/users/updateprofile`, { field, value });
  },
  sendEmailUpdateCode: () => {
    return handleRequest(`post`, `/users/sendemailverification`, { newEmail: "/" });
  },
  updateEmail: ({ code, newEmail }) => {
    return handleRequest(`post`, `/users/updateEmail`, { code, newEmail });
  },
  updatePassword: ({ currentPassword, newPassword }) => {
    return handleRequest(`post`, `/users/updatepassword`, { currentPassword, newPassword });
  },
  updateDeviceField: ({ sn, field, value }) => {
    return handleRequest(`post`, `/devices/updatefield`, { sn, field, value });
  },
  updateDeviceImage: ({ base64Image, sn }) => {
    return handleRequest(`post`, `/devices/updateimage`, { sn, base64Image });
  },
  updateProjectField: ({ id, field, value }) => {
    return handleRequest(`post`, `/projects/updatefield`, { id, field, value });
  },
  updateProjectImage: ({ base64Image, id }) => {
    return handleRequest(`post`, `/projects/updateimage`, { id, base64Image });
  },
  updateAlertSettings: ({ sn, alerts }) => {
    return handleRequest(`post`, `/alertsetting/updatebydevicesn`, { sn, alerts });
  },
  openWaterValve: ({ sn, seconds }) => {
    return handleRequest(`post`, `/send-relay-on-command`, { sn, seconds });
  },
  closeWaterValve: ({ sn }) => {
    return handleRequest(`post`, `/send-relay-off-command`, { sn });
  },
  getWaterValveSettings: ({ sn }) => {
    return handleRequest(`post`, `/deviceauto/getwatervalvebydevicesn`, { sn });
  },
  getWaterValveHistory: ({ sn }) => {
    return handleRequest(`post`, `/deviceautohistory/getrecentwatervalvebysn`, { sn });
  },
  getAlertHistory: ({ sn }) => {
    return handleRequest(`post`, `/alert/getpendingbydevicesn`, { sn });
  },
  updateWaterValveSettings: ({ sn, selectedHours, openSeconds }) => {
    return handleRequest(`post`, `/deviceauto/updatewatervalvebydevicesn`, {
      sn,
      selectedHours,
      openSeconds,
    });
  },
  addProjectWithRelation: ({ alias, lat, lng, status, img }) => {
    return handleRequest(`post`, `/projects/addwithrelation`, { alias, lat, lng, status, img });
  },
  addDeviceWithProjectAndImage: ({
    address,
    alias,
    type,
    series,
    seriesAbbr,
    authorized,
    city,
    country,
    district,
    lat,
    lng,
    province,
    report_cycle,
    sn,
    status,
    projectId,
    img,
  }) => {
    return handleRequest(`post`, `/devices/addwithprojectandimage`, {
      address,
      alias,
      type,
      series,
      seriesAbbr,
      authorized,
      city,
      country,
      district,
      lat,
      lng,
      province,
      report_cycle,
      sn,
      status,
      projectId,
      img,
    });
  },
};

export default biosenseAPI;

export function handleError(error) {
  Swal.fire("錯誤", `<div style='flex flex-start'>錯誤信息: ${error}</div>`, "error");
}
